import { bearingDeg, toLocalMetres } from './geo.ts';
import type { LngLat } from './types';

export interface HolePoint { x: number; y: number }

/**
 * Build the projection that draws a hole running straight up the screen.
 *
 * Returns a function from a coordinate to metres in "hole space": x across the
 * hole (positive right), y up the screen in SVG's sense (NEGATIVE is up, so the
 * green lands above the tee). The tee is the origin.
 *
 * The maths, because the sign is easy to get backwards and the failure is
 * subtle — the hole simply renders on a diagonal, which looks like a styling
 * bug rather than a projection one:
 *
 *   tee->green at bearing b is (east, north) = (d·sin b, d·cos b)
 *   rotating the frame BY b gives east' = E·cos b − N·sin b  ->  0
 *                                north' = N·cos b + E·sin b  ->  d
 *   screen y = −north', because SVG y grows downward
 *
 * Rotating by −b instead shears every hole onto a diagonal and puts the tee at
 * the top. There is a test for exactly that.
 */
export function holeFrame(tee: LngLat, green: LngLat): (p: LngLat) => HolePoint {
  const b = (bearingDeg(tee, green) * Math.PI) / 180;
  const cos = Math.cos(b);
  const sin = Math.sin(b);
  return (p: LngLat) => {
    const { east, north } = toLocalMetres(tee, p);
    return {
      x: east * cos - north * sin,
      y: -(north * cos + east * sin),
    };
  };
}
